import { Fragment, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Text } from '@fly/core';

const MAX_VISIBLE_TAGS = 3;

type ArtifactVersionCoTagsProps = {
  versionName: string;
  coTags: string[];
  expanded: boolean;
  onToggleExpanded: (event: MouseEvent) => void;
};

export function ArtifactVersionCoTags({
  versionName,
  coTags,
  expanded,
  onToggleExpanded,
}: ArtifactVersionCoTagsProps) {
  const { t } = useTranslation();

  const tags = [...coTags, versionName];

  const hasMore = tags.length > MAX_VISIBLE_TAGS;
  const visibleTags =
    expanded || !hasMore ? tags : tags.slice(0, MAX_VISIBLE_TAGS);
  const remainingCount = tags.length - MAX_VISIBLE_TAGS;

  const handleToggleExpanded = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleExpanded(event);
  };
  return (
    <span className='text-slateA-11 inline-flex flex-wrap items-center gap-fly-1 gap-fly-2 text-size-fly-2'>
      {visibleTags.map((tag, index) => (
        <Fragment key={tag}>
          {index > 0 && ', '}
          <Badge
            variant='soft'
            color='neutral'
            size='2'
            className='min-w-0 flex'
          >
            <Text className='truncate'>{tag}</Text>
          </Badge>
        </Fragment>
      ))}
      {hasMore && (
        <Text
          size='2'
          className='font-semibold cursor-pointer'
          data-testid='artifact-version-co-tags-toggle'
          onClick={handleToggleExpanded}
        >
          {expanded
            ? t('app.common.actions.show_less')
            : t('app.dashboard.artifacts.co_tags_more', {
                count: remainingCount,
                defaultValue: '& {{count}} more',
              })}
        </Text>
      )}
    </span>
  );
}
